import React, { useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ReferenceLine, ReferenceArea } from 'recharts';
import { getQuadrantInfo, quadrantColors, quadrantDescriptions } from '../utils/dashboardUtils';

const NineBoxChart = ({ nineBoxData }) => {
  const [showLegend, setShowLegend] = useState(false);

  const processedData = nineBoxData.map(item => {
    const quadrant = getQuadrantInfo(item.desempenho, item.potencial);
    return {
      x: item.desempenho,
      y: item.potencial,
      name: item.nome_pessoa,
      quadrant: quadrant.name,
      quadrantKey: quadrant.key,
      coords: quadrant.coords
    };
  });

  let filteredData = processedData;

  if (filteredData.length === 0 && processedData.length === 0) {
    filteredData = [
      {
        x: 1.5,
        y: 1.5,
        name: 'Exemplo',
        quadrant: 'Mantenedor',
        quadrantKey: 'mantenedor',
        coords: 'X: 1-2, Y: 1-2',
        isDummy: true // Adicionar uma flag para dados de exemplo
      }
    ];
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      if (data && data.isDummy) {
        return null;
      }

      return (
        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{data.name}</p>
          <p className="text-sm text-gray-600">X: {data.x.toFixed(2)} | Y: {data.y.toFixed(2)}</p>
          <p className="text-sm font-medium" style={{ color: quadrantColors[data.quadrantKey] }}>
            {data.quadrant}
          </p>
          <p className="text-xs text-gray-500 mt-1">{data.coords}</p>
        </div>
      );
    }
    return null;
  };

  const LegendModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-4xl max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Legenda dos Quadrantes</h3>
          <button
            onClick={() => setShowLegend(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(quadrantColors).map(([key, color]) => {
            const quadrantName = key === 'talento-chave' ? 'Talento-Chave' :
                               key === 'diamante-lapidado' ? 'Diamante Lapidado' :
                               key === 'enigma' ? 'Enigma' :
                               key === 'pilar' ? 'Pilar' :
                               key === 'mantenedor' ? 'Mantenedor' :
                               key === 'risco' ? 'Risco' :
                               key === 'cumpridor' ? 'Cumpridor' :
                               key === 'eficaz' ? 'Eficaz' :
                               'Insuficiente';

            const sampleCoords = key === 'talento-chave' ? [2.5, 2.5] :
                               key === 'diamante-lapidado' ? [1.5, 2.5] :
                               key === 'enigma' ? [0.5, 2.5] :
                               key === 'pilar' ? [2.5, 1.5] :
                               key === 'mantenedor' ? [1.5, 1.5] :
                               key === 'risco' ? [0.5, 1.5] :
                               key === 'cumpridor' ? [2.5, 0.5] :
                               key === 'eficaz' ? [1.5, 0.5] : [0.5, 0.5];

            return (
              <div key={key} className="border border-gray-200 rounded p-3">
                <div className="flex items-center mb-2">
                  <div
                    className="w-4 h-4 rounded mr-2"
                    style={{ backgroundColor: color }}
                  ></div>
                  <span className="font-medium text-sm">{quadrantName}</span>
                </div>
                <p className="text-xs text-gray-600 mb-1">
                  {getQuadrantInfo(sampleCoords[0], sampleCoords[1]).coords}
                </p>
                <p className="text-xs text-gray-700">{quadrantDescriptions[key]}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Matriz Nine Box (Talent Matrix)</h3>
        <button
          onClick={() => setShowLegend(true)}
          className="text-gray-500 hover:text-gray-700 p-1"
          title="Ver legenda detalhada"
        >
          ❓
        </button>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart data={filteredData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <ReferenceArea x1={0} x2={1} y1={0} y2={1} fill="#dc3545" />
          <ReferenceArea x1={1} x2={2} y1={0} y2={1} fill="#ffc99a" />
          <ReferenceArea x1={2} x2={3} y1={0} y2={1} fill="#fff3cd" />
          <ReferenceArea x1={0} x2={1} y1={1} y2={2} fill="#fd7e14" />
          <ReferenceArea x1={1} x2={2} y1={1} y2={2} fill="#007bff" />
          <ReferenceArea x1={2} x2={3} y1={1} y2={2} fill="#20c997" />
          <ReferenceArea x1={0} x2={1} y1={2} y2={3} fill="#ffc107" />
          <ReferenceArea x1={1} x2={2} y1={2} y2={3} fill="#77dd77" />
          <ReferenceArea x1={2} x2={3} y1={2} y2={3} fill="#28a745" />
          <g>
            <text x="16.67%" y="83.33%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Insuficiente</text>
            <text x="50%" y="83.33%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Eficaz</text>
            <text x="83.33%" y="83.33%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Cumpridor</text>
            <text x="16.67%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Risco</text>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Mantenedor</text>
            <text x="83.33%" y="50%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Pilar</text>
            <text x="16.67%" y="16.67%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Enigma</text>
            <text x="50%" y="16.67%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Diamante Lapidado</text>
            <text x="83.33%" y="16.67%" textAnchor="middle" dominantBaseline="middle" className="text-sm font-bold fill-black" style={{textShadow: '2px 2px 4px rgba(255,255,255,0.9)', fontSize: '12px'}}>Talento-Chave</text>
          </g>
          <ReferenceLine x={1} stroke="#6b7280" strokeWidth={2} />
          <ReferenceLine x={2} stroke="#6b7280" strokeWidth={2} />
          <ReferenceLine y={1} stroke="#6b7280" strokeWidth={2} />
          <ReferenceLine y={2} stroke="#6b7280" strokeWidth={2} />
          <XAxis
            type="number"
            dataKey="x"
            domain={[0, 3]}
            label={{ value: 'Desempenho', position: 'insideBottom', offset: -5 }}
            tickCount={4}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[0, 3]}
            label={{ value: 'Potencial', angle: -90, position: 'insideLeft' }}
            tickCount={4}
          />
          <Tooltip content={<CustomTooltip />} />
          <Scatter
            dataKey="y"
            fill="#1e40af"
            stroke="#fff"
            strokeWidth={2}
            r={6}
          />
        </ScatterChart>
      </ResponsiveContainer>
      {showLegend && <LegendModal />}
    </div>
  );
};

export default NineBoxChart;